/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { useState, useLayoutEffect, useMemo, useRef } from 'react';
import { animated, useSpring } from '@react-spring/web';

import {
  Text,
  Button,
  ThemeProvider,
  getThemeByName,
  useScrollTo,
  Flex,
  Box,
} from '.';

import type { ItemGroup, BoxProps } from '.';

const getOffsetY = (element: HTMLElement) =>
  element.offsetTop + element.offsetHeight / 2 - 36;

export interface ListGroupNavProps<Item>
  extends Omit<BoxProps, 'ref' | 'onChange'> {
  groups: ReadonlyArray<ItemGroup<Item>>;
  activeGroup?: ItemGroup<Item> | null;
  onActiveGroupChange?: (group: ItemGroup<Item> | null) => void;
}

/**
 * A single column grid of navigation links, one link per group.
 *
 * Links will be active whenever their group name matches the location hash.
 * Groups that have no items will be 'disabled' (text instead of links).
 *
 * This component supports groups as defined by `createListView`.
 *
 * @see https://tishman.netlify.app/guides/list-views
 * @see https://tishman.netlify.app/hooks/create-list-view
 * @see https://tishman.netlify.app/components/xx-list-nav
 */
export function ListGroupNav<Item>({
  groups,
  activeGroup,
  onActiveGroupChange,
  sx,
  ...props
}: ListGroupNavProps<Item>): JSX.Element {
  // A map of groups to their button elements.
  const [elements] = useState(
    () => new WeakMap<ItemGroup<Item>, HTMLElement>(),
  );

  // Scroll to the active group button.
  const domTarget = useRef<HTMLDivElement>(null);
  const scrollTo = useScrollTo({ domTarget });

  // Animate the indicator position to align with the active group button.
  const [indicatorTransform, animateIndicator] = useSpring(() => ({ y: 0 }));
  useLayoutEffect(() => {
    const element = activeGroup ? elements.get(activeGroup) : null;
    if (element) {
      scrollTo(element);
      void animateIndicator({ y: getOffsetY(element) });
    }
  });

  /** A small circle icon that represents the active group. */
  const indicator = useMemo(
    () => (
      <animated.svg
        aria-hidden
        height="8"
        style={indicatorTransform}
        sx={{
          position: 'relative',
          left: '8px',
          color: 'text',
        }}
        viewBox="0 0 8 8"
        width="8"
      >
        <circle cx="4" cy="4" fill="currentColor" r="4" />
      </animated.svg>
    ),
    [indicatorTransform],
  );

  return (
    <ThemeProvider theme={getThemeByName('Rock Center Black')}>
      <Box
        bg="background"
        sx={{
          'overflowY': 'scroll',
          'scrollbarWidth': 'none',
          '::-webkit-scrollbar': {
            display: 'none',
          },
          ...sx,
        }}
        {...props}
      >
        <Flex
          as="ol"
          pl={1}
          py={4}
          ref={domTarget}
          sx={{
            justifyContent: 'space-between',
            height: '100%',
            flexDirection: 'column',
            textAlign: 'center',
            position: 'relative',
          }}
        >
          <Flex
            pl={['9px', null, '12px']}
            pr={['24px', null, '32px']}
            sx={{ position: 'absolute' }}
          >
            <span sx={{ visibility: 'hidden' }}>{groups[0].name}</span>
            {indicator}
          </Flex>
          {groups.map(
            (group) =>
              group.name && (
                <li
                  key={group.name}
                  ref={
                    group.hasItems
                      ? (element) => element && elements.set(group, element)
                      : null
                  }
                >
                  {group.hasItems ? (
                    <Button
                      //a11y: aria-label is for screen reader, for '#' we need to put some other detail, because it's not a alphabet
                      aria-label={`Index, letter ${group.name}`}
                      aria-selected={group === activeGroup}
                      data-active={group === activeGroup}
                      onClick={() => onActiveGroupChange?.(group)}
                      p={0}
                      pl={['9px', null, '12px']}
                      pr={['24px', null, '32px']}
                      variant="listGroupNav"
                    >
                      {group.name}
                    </Button>
                  ) : (
                    <Text
                      aria-disabled={true}
                      aria-label={`Index, letter ${group.name}`}
                      as="span"
                      data-active={false}
                      p={0}
                      pl={['9px', null, '12px']}
                      pr={['24px', null, '32px']}
                      role="button"
                      variant="groupList"
                    >
                      {group.name}
                    </Text>
                  )}
                </li>
              ),
          )}
        </Flex>
      </Box>
    </ThemeProvider>
  );
}
